/**
 * DashboardLayout — Layout principal del panel de la app.
 * Iter.246 · Fase 8 UX Refactor:
 *   Reducido de 704 a ~200 líneas extrayendo componentes a `components/layout/`:
 *   - `Sidebar.jsx`        → aside completo con logo, switcher, nav, footer.
 *   - `Topbar.jsx`         → header sticky con title + widgets.
 *   - `RestaurantSwitcher` → select con restaurantes del usuario.
 *   - `UserMenu`           → avatar dropdown con settings/guide/logout.
 *   - `CreditWidget`       → pill de créditos IA con HoverCard.
 *   - `AIActivityIndicator`→ pill de tareas IA activas.
 *   - `UIModeToggle`       → toggle Simple/Avanzado (iter.245).
 *   - `NAV_ITEMS`          → movido a `lib/navItems.js`.
 *   - `ROLE_*`             → movido a `lib/roleMeta.js`.
 *
 * Responsabilidad de este archivo: composición + estado global + effects.
 */
import { useState, useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import api from "../lib/api";
import { NAV_ITEMS } from "../lib/navItems";
import { RestaurantProvider, useRestaurant } from "../context/RestaurantContext";
import { ImageGenerationProvider, useImageGeneration } from "../context/ImageGenerationContext";
import { MobileBottomNav } from "../components/MobileBottomNav";
import { ErrorBoundary } from "../components/ErrorBoundary";
import HelpChatbot from "../components/HelpChatbot";
import OnboardingManager from "../components/OnboardingManager";
import InstallBanner from "../components/pwa/InstallBanner";
import BatchProgressDock from "../components/BatchProgressDock";
import useFiscalUnresolved from "../hooks/useFiscalUnresolved";
import { useSiteConfig } from "../hooks/useSiteConfig";
import useDraftOrders from "../hooks/useDraftOrders";
import useShoppingListsPending from "../hooks/useShoppingListsPending";
import { usePlanFeatures } from "../hooks/usePlanFeatures";
import { useBonusMilestoneToasts } from "../hooks/useBonusMilestoneToasts";
import useModuleUnlockNudge from "../hooks/useModuleUnlockNudge";

import { Sidebar } from "../components/layout/Sidebar";
import { Topbar } from "../components/layout/Topbar";
import { ThemeSync } from "../components/theme/ThemeSync";

function DashboardContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("mg_sidebar_collapsed") === "true"; } catch { return false; }
  });
  const [user, setUser] = useState(null);
  const [creditInfo, setCreditInfo] = useState(null);
  const [creditBreakdown, setCreditBreakdown] = useState(null);
  // iter.247 — flag para evitar guardar el estado inicial del hydrate
  // (evita PATCH innecesario cuando aplicamos user.preferences.sidebar_collapsed).
  const collapseHydrated = useRef(false);

  const { restaurants, currentRestaurant, selectRestaurant, loading: restaurantsLoading } = useRestaurant();
  const { userGuideVisible } = useSiteConfig();

  // Badges
  const { count: fiscalUnresolvedCount } = useFiscalUnresolved(currentRestaurant?.restaurant_id);
  const { count: draftOrdersCount } = useDraftOrders(currentRestaurant?.restaurant_id);
  const { count: shoppingListsPendingCount } = useShoppingListsPending(currentRestaurant?.restaurant_id);
  const { setCurrentRestaurantId } = useImageGeneration();

  // Sync current restaurant id to the image generation context for polling
  useEffect(() => {
    setCurrentRestaurantId(currentRestaurant?.restaurant_id || null);
  }, [currentRestaurant?.restaurant_id, setCurrentRestaurantId]);

  // Derive current role from the restaurant's `role` field (injected by backend)
  const currentRole = currentRestaurant?.role || "owner";
  const { hasModule, planId, loading: planLoading } = usePlanFeatures();

  // Only consider a user "Starter" once features have actually loaded — the hook
  // defaults to plan_starter while loading, which would otherwise misroute every
  // freshly logged-in user away from the Dashboard.
  const isStarter = !planLoading && planId === "plan_starter";

  // Starter shows ONLY the modules they have unlocked (no lock icons / no clutter).
  const navItems = NAV_ITEMS
    .filter((item) => item.roles.includes(currentRole))
    .filter((item) => !isStarter || hasModule(item.moduleKey));

  // ─── Editable sidebar order persistence ────────────────────────────────
  // NOTE: drag-and-drop reorder logic is currently disabled at the render layer.
  // The user's saved order (`user.preferences.sidebar_order`) is still fetched
  // and available to future consumers, but the UI does not surface handles.
  // Placeholder retained so downstream refactors know the intent.

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await api.getMe();
        setUser(userData);
        // iter.247 · Fase 8 pulido — cross-device sidebar state:
        // si el usuario tiene preferencia guardada en el servidor, aplicarla.
        // Si no hay preferencia server-side, mantener el valor de localStorage.
        const serverPref = userData?.preferences?.sidebar_collapsed;
        if (typeof serverPref === "boolean") {
          setCollapsed(serverPref);
          try { localStorage.setItem("mg_sidebar_collapsed", String(serverPref)); } catch { /* storage blocked */ }
        }
        collapseHydrated.current = true;
      } catch (error) {
        console.error("Failed to fetch user:", error);
        collapseHydrated.current = true;
      }
    };
    fetchUser();
  }, []);

  // Fetch credit balance — refreshed on AI endpoints (credits-changed event)
  // plus a 60s safety interval.
  useEffect(() => {
    if (!currentRestaurant?.restaurant_id) return;
    const fetchCredits = async () => {
      try {
        const [data, stats] = await Promise.all([
          api.get(`/subscription/my/${currentRestaurant.restaurant_id}`),
          api.get(`/subscription/credits/${currentRestaurant.restaurant_id}/usage-stats?days=7`).catch(() => null),
        ]);
        setCreditInfo(data?.subscription || null);
        setCreditBreakdown(stats || null);
      } catch { /* ignore */ }
    };
    fetchCredits();
    const onCreditsChanged = () => fetchCredits();
    window.addEventListener("credits-changed", onCreditsChanged);
    const iv = setInterval(fetchCredits, 60000);
    return () => {
      clearInterval(iv);
      window.removeEventListener("credits-changed", onCreditsChanged);
    };
  }, [currentRestaurant?.restaurant_id]);

  // Fire friendly milestone toasts as the onboarding bonus depletes
  useBonusMilestoneToasts(creditInfo);

  // Onboarding gradual: nudge to enable groups when the restaurant matures.
  useModuleUnlockNudge({
    restaurantId: currentRestaurant?.restaurant_id,
    role: currentRole,
  });

  // ─── Route guard: redirect if role doesn't have access to current route ───
  useEffect(() => {
    if (!currentRole || !currentRestaurant) return;
    const path = location.pathname;
    // Starter users land on Customers, not Dashboard (they don't have it).
    if (isStarter && (path === "/app" || path === "/app/")) {
      navigate("/app/customers", { replace: true });
      return;
    }
    const matchedItem = NAV_ITEMS.find((item) => {
      if (item.exact) return path === item.path;
      return path.startsWith(item.path) && item.path !== "/app";
    });
    if (matchedItem && !matchedItem.roles.includes(currentRole)) {
      const firstAllowed = navItems[0];
      if (firstAllowed) {
        navigate(firstAllowed.path, { replace: true });
        toast.error("No tienes permiso para acceder a esa seccion");
      }
    }
  }, [location.pathname, currentRole, currentRestaurant, isStarter, navigate, navItems]);

  const handleLogout = async () => {
    try {
      await api.logout();
      toast.success("Sesión cerrada");
      navigate("/");
    } catch {
      navigate("/");
    }
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem("mg_sidebar_collapsed", String(next)); } catch { /* storage blocked */ }
      // iter.247 · Fase 8 pulido — sync cross-device via user.preferences.
      // Solo cuando ya se hidrató (evita PATCH duplicado en mount).
      if (collapseHydrated.current) {
        api.request("/auth/me/preferences", {
          method: "PATCH",
          body: JSON.stringify({ sidebar_collapsed: next }),
        }).catch(() => { /* silent — localStorage sigue siendo el fallback */ });
      }
      return next;
    });
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Preserve local hooks: no orphan refs needed post-refactor.

  // Show loading or redirect if no restaurant
  if (!restaurantsLoading && restaurants.length === 0) {
    navigate("/onboarding");
    return null;
  }

  const pageTitle =
    navItems.find((item) => isActive(item.path, item.exact))?.label || "Panel de Control";

  return (
    <div className="min-h-screen bg-background flex">
      {/* Cross-device theme sync (light/dark/system) — iter.248 Fase 10 */}
      <ThemeSync />

      {/* Sidebar (aside) */}
      <Sidebar
        sidebarOpen={sidebarOpen}
        onCloseMobile={() => setSidebarOpen(false)}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        restaurants={restaurants}
        currentRestaurant={currentRestaurant}
        onSelectRestaurant={selectRestaurant}
        currentRole={currentRole}
        isStarter={isStarter}
        hasModule={hasModule}
        badges={{
          draftOrders: draftOrdersCount,
          fiscalUnresolved: fiscalUnresolvedCount,
          shoppingListsPending: shoppingListsPendingCount,
        }}
        user={user}
        userGuideVisible={userGuideVisible}
        onLogout={handleLogout}
      />

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" /* z-stack-doc: L4 mobile sidebar overlay */
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content area —
          CRITICAL CSS NOTE (Feb 2026 iter.211 responsive fix):
          • `min-w-0` is REQUIRED on a flex-1 child whose content can be wider
            than its parent. Without it, Flexbox uses the intrinsic min-content
            width and forces the flex item to exceed the viewport.
          • `overflow-x-hidden` on <main> is a defensive belt in case a future
            child re-introduces overflow. */}
      <div
        className={`flex-1 min-w-0 transition-all duration-200 ease-in-out ${
          collapsed ? "lg:ml-[68px]" : "lg:ml-64"
        }`}
      >
        <Topbar
          pageTitle={pageTitle}
          onOpenMobileMenu={() => setSidebarOpen(true)}
          currentRestaurant={currentRestaurant}
          isStarter={isStarter}
          creditInfo={creditInfo}
          creditBreakdown={creditBreakdown}
        />

        {/* Page content — extra bottom padding on mobile for the nav bar */}
        <main className="p-4 lg:p-8 pb-20 lg:pb-8 overflow-x-hidden">
          <ErrorBoundary>
            <Outlet
              context={{
                currentRestaurant,
                restaurants,
                currentRole,
                user,
                sidebarCollapsed: collapsed,
              }}
            />
          </ErrorBoundary>
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <MobileBottomNav onMoreClick={() => setSidebarOpen(true)} />

      {/* Global overlays / helpers */}
      <HelpChatbot />
      <OnboardingManager />
      <InstallBanner />
      <BatchProgressDock />
    </div>
  );
}

export default function DashboardLayout() {
  return (
    <RestaurantProvider>
      <ImageGenerationProvider>
        <DashboardContent />
      </ImageGenerationProvider>
    </RestaurantProvider>
  );
}
